import {
  ArrowUpRight,
  Bot,
  Check,
  Cloud,
  Copy,
  HelpCircle,
  KeyRound,
  type LucideIcon,
  Maximize2,
  MonitorCheck,
  PlayCircle,
  Rocket,
  Route,
  Settings,
  ShieldCheck,
  Sparkles,
  Terminal,
  Trash2,
  XIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Github } from "@/components/icons/github";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useVersion } from "@/lib/api";

import imgPostShip from "./assets/postship-projects-grid.mp4";
import imgStep1 from "./assets/step1-create-form.mp4";
import imgStep2 from "./assets/step2-provisioning.mp4";
import imgStep3 from "./assets/step3-setup-in-claude.mp4";
import imgStep4 from "./assets/step4-vibecode.mp4";
import imgStep5 from "./assets/step5-push-deploy.mp4";
import imgStep6 from "./assets/step6-live-app.jpg";

/* ------------------------------------------------------------------ */
/*  Shared components                                                  */
/* ------------------------------------------------------------------ */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-muted-foreground hover:text-foreground absolute top-2 right-2 rounded p-1 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="size-3.5 text-green-500" /> : <Copy className="size-3.5" />}
    </button>
  );
}

function CodeBlock({ code }: { code: string }) {
  return (
    <div className="relative">
      <CopyButton text={code} />
      <pre className="bg-primary/5 overflow-x-auto rounded-md p-4 text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="bg-accent/10 text-accent flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold">
        {number}
      </div>
      <div className="flex-1 pt-0.5">
        <h4 className="text-primary mb-1 font-bold">{title}</h4>
        <div className="text-muted-foreground text-sm">{children}</div>
      </div>
    </div>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-accent/5 border-accent/30 rounded-md border-l-4 px-4 py-3">
      <div className="flex items-start gap-2">
        <Sparkles className="text-accent mt-0.5 size-4 shrink-0" />
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
    >
      {children}
      <ArrowUpRight className="size-3" />
    </a>
  );
}

function BenefitCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="border-border rounded-lg border p-4">
      <div className="bg-accent/10 mb-3 inline-flex rounded-lg p-2">
        <Icon className="text-accent size-5" />
      </div>
      <h4 className="text-primary mb-1 font-bold">{title}</h4>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  );
}

function Screenshot({ src, alt }: { src: string; alt: string }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setExpanded(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [expanded]);

  return (
    <>
      <div
        className="mx-auto max-w-2xl cursor-zoom-in overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow-md"
        onClick={() => setExpanded(true)}
      >
        <img src={src} alt={alt} loading="lazy" className="h-auto w-full" />
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setExpanded(false)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={src}
              alt={alt}
              className="border-border max-h-[90vh] max-w-[95vw] rounded-xl border shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="bg-card text-muted-foreground hover:bg-secondary absolute top-2 right-2 rounded-full p-1.5 shadow-md transition-colors"
            >
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** A muted, autoplaying, looping video (GIF-like) that expands into a larger dialog on click. */
function VideoDemo({ src, label }: { src: string; label: string }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setExpanded(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [expanded]);

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label={`Enlarge: ${label}`}
        className="border-border group relative mx-auto block w-full max-w-2xl cursor-zoom-in overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow-md"
      >
        <video
          src={src}
          aria-label={label}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="h-auto w-full"
        />
        <span className="bg-background/80 text-muted-foreground pointer-events-none absolute right-2 bottom-2 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
          <Maximize2 className="size-3" />
          Click to enlarge
        </span>
      </button>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setExpanded(false)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <video
              src={src}
              aria-label={label}
              autoPlay
              loop
              muted
              playsInline
              controls
              className="border-border max-h-[90vh] max-w-[95vw] rounded-xl border shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="bg-card text-muted-foreground hover:bg-secondary absolute top-2 right-2 rounded-full p-1.5 shadow-md transition-colors"
            >
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** A step in the six-step flow: number + title + copy on the left, demo on the right. */
function FlowStep({
  number,
  title,
  intro,
  points,
  img,
  imgAlt,
  isVideo,
}: {
  number: number;
  title: string;
  intro: string;
  points: string[];
  img: string;
  imgAlt: string;
  isVideo?: boolean;
}) {
  return (
    <div className="border-border rounded-lg border p-5">
      <div className="grid gap-6 md:grid-cols-2 md:items-center">
        <div>
          <Step number={number} title={title}>
            <p className="mb-2">{intro}</p>
            <ul className="list-disc space-y-1 pl-5">
              {points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Step>
        </div>
        {isVideo ? <VideoDemo src={img} label={imgAlt} /> : <Screenshot src={img} alt={imgAlt} />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section type & data                                                */
/* ------------------------------------------------------------------ */

/**
 * "Keep an eye on your monthly budget" -- only on an instance that gives new AI
 * projects their own services gateway keys (`svc_gateway_enabled`).
 */
function BudgetStep() {
  const { data } = useVersion();
  if (!data?.svc_gateway_enabled) {
    return null;
  }
  return (
    <Step number={5} title="Keep an eye on your monthly budget">
      <p>
        Newer AI-enabled projects get their own keys for web search, tracing and the models, each
        with a monthly budget. The project&apos;s Details tab shows this month&apos;s use under{" "}
        <strong>AI services (dev)</strong>. When a budget is used up, those calls stop working until
        next month. Need more? Click <strong>Request more</strong>, say what it is for, and an admin
        decides. Your local runs count against the same budget.
      </p>
      <p>
        An admin can also pause a service, or the whole project, for a while &mdash; for example
        when a loop is running up the bill. The section then says{" "}
        <strong>Suspended by an admin</strong> with the reason, and those calls fail until an admin
        resumes them. Nothing about your keys changes; ask the admin who paused it.
      </p>
    </Step>
  );
}

export interface SearchItem {
  title: string;
  keywords: string[];
  /** Only on an instance with services gateway keys (`svc_gateway_enabled`). */
  servicesGatewayOnly?: boolean;
}

export interface HowToSection {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  content: React.ReactNode;
  searchItems: SearchItem[];
}

const FLOW_OVERVIEW: { step: string; label: string; sub: string }[] = [
  { step: "Step 1", label: "Create", sub: "Name it in Scaffold UI" },
  { step: "Step 2", label: "Set up", sub: "Repo + hosting + login" },
  { step: "Step 3", label: "Open in Claude", sub: "Paste the prompt" },
  { step: "Step 4", label: "Build", sub: "Vibecode it locally" },
  { step: "Step 5", label: "Push", sub: "Pushing is deploying" },
  { step: "Step 6", label: "Live", sub: "A shareable URL" },
];

export const SECTIONS: HowToSection[] = [
  /* ---- 1. Video walkthrough ---- */
  {
    id: "video",
    title: "Video walkthrough",
    icon: PlayCircle,
    description: "Watch the whole flow end to end",
    content: (
      <div className="space-y-4">
        <div className="border-border relative aspect-video w-full overflow-hidden rounded-xl border shadow-sm">
          <iframe
            src="https://share.synthesia.io/embeds/videos/86e349ee-1d86-427c-9ced-f5cba4592d35"
            title="Norican Scaffold Demo"
            loading="lazy"
            allow="encrypted-media; fullscreen; microphone;"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
        <p className="text-muted-foreground text-sm">
          A full walkthrough of the whole flow, from idea to live app. The guide below covers the
          same steps with screenshots.
        </p>
      </div>
    ),
    searchItems: [
      { title: "Video walkthrough", keywords: ["video", "tutorial", "walkthrough", "watch"] },
    ],
  },

  /* ---- 2. From idea to live app (the promise) ---- */
  {
    id: "overview",
    title: "From idea to live app",
    icon: Sparkles,
    description: "What Scaffold UI does, and why it is fast",
    content: (
      <div className="space-y-6">
        <div>
          <h3 className="text-primary mb-2 text-xl font-bold">
            You focus on the idea. We handle the rest.
          </h3>
          <p className="text-muted-foreground">
            Building an internal tool used to mean weeks of setup before the first feature. That
            part is gone. Describe what you want to build, and Scaffold UI gives you a real,
            production-ready app you can keep shaping.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <BenefitCard
            icon={Settings}
            title="No setup"
            description="No repo, no environment, no config to wrangle."
          />
          <BenefitCard
            icon={Cloud}
            title="No hosting"
            description="Servers, deploys, and URLs are handled for you."
          />
          <BenefitCard
            icon={Rocket}
            title="No DevOps"
            description="Push your changes and it ships itself."
          />
        </div>

        <Tip>
          Scaffold UI is built for Norican colleagues. If you have questions or get stuck at any
          point, reach out to the Norican team.
        </Tip>
      </div>
    ),
    searchItems: [
      { title: "From idea to live app", keywords: ["overview", "about", "promise", "what"] },
      { title: "No setup, no hosting, no DevOps", keywords: ["setup", "hosting", "devops", "why"] },
    ],
  },

  /* ---- 3. Your desktop is ready (IT set up) ---- */
  {
    id: "desktop",
    title: "Your desktop is ready",
    icon: MonitorCheck,
    description: "What IT already set up for you",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Your IT team already prepared your desktop. Your PC has been prepped with the essential
          toolkit for vibe coding, so there is nothing for you to install before you start.
        </p>

        <div className="grid gap-4 sm:grid-cols-3">
          <BenefitCard
            icon={Sparkles}
            title="Claude Desktop + account"
            description="The AI assistant you build with, already signed in."
          />
          <BenefitCard
            icon={Terminal}
            title="Ubuntu WSL"
            description="The Linux environment your project runs in on Windows."
          />
          <BenefitCard
            icon={Github}
            title="GitHub CLI + account"
            description="Signed in, so your code can be pushed to Norican's GitHub."
          />
        </div>

        <Tip>
          If anything on this list seems to be missing on your machine, contact Norican IT before
          you begin, you should not need to install these yourself.
        </Tip>
      </div>
    ),
    searchItems: [
      {
        title: "Your desktop is ready",
        keywords: ["it", "setup", "desktop", "install", "prepared", "toolkit"],
      },
      { title: "Claude Desktop", keywords: ["claude", "desktop", "ai", "assistant"] },
      { title: "Ubuntu WSL", keywords: ["ubuntu", "wsl", "linux", "windows"] },
      { title: "GitHub CLI", keywords: ["github", "cli", "gh", "account"] },
    ],
  },

  /* ---- 4. The whole flow (6 steps) ---- */
  {
    id: "flow",
    title: "The whole flow",
    icon: Route,
    description: "Idea to live app in six steps",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Idea to live app, with no setup in between. Here is the whole thing, start to finish.
        </p>

        {/* Six-step overview */}
        <div className="border-border grid gap-3 rounded-lg border p-4 sm:grid-cols-3 lg:grid-cols-6">
          {FLOW_OVERVIEW.map((s) => (
            <div key={s.label} className="flex flex-col gap-1">
              <span className="text-accent text-xs font-semibold">{s.step}</span>
              <span className="text-primary font-bold">{s.label}</span>
              <span className="text-muted-foreground text-xs">{s.sub}</span>
            </div>
          ))}
        </div>

        {/* Detailed steps */}
        <div className="space-y-5">
          <FlowStep
            number={1}
            title="Create your project"
            intro="Open Scaffold UI, sign in with your Norican account, connect your GitHub, and start a new project."
            points={[
              "Click New Project",
              "Give it a name and a short description",
              "Share it with your team",
              "Hit create, done in seconds",
            ]}
            isVideo
            img={imgStep1}
            imgAlt="The New Project review and confirm form"
          />
          <FlowStep
            number={2}
            title="Watch it set itself up"
            intro="One click provisions everything a real app needs. You just watch it happen."
            points={[
              "A GitHub repo is created",
              "Hosting and login are wired up",
              "Nothing to configure by hand (this can take up to 15 minutes)",
            ]}
            isVideo
            img={imgStep2}
            imgAlt="The creation dialog showing the setup steps"
          />
          <FlowStep
            number={3}
            title="Open it in Claude"
            intro="Your project ships with a ready-made prompt to pull it onto your machine."
            points={[
              "Copy the “Set up in Claude” prompt from the project page",
              "Paste it into Claude",
              "Choose your Ubuntu WSL and a folder location",
              "Claude clones and opens the project locally, and installs anything missing",
            ]}
            isVideo
            img={imgStep3}
            imgAlt="The project page with the Set up in Claude prompt"
          />
          <FlowStep
            number={4}
            title="Build it by vibecoding"
            intro="Run it locally and describe what you want in plain English. Claude builds it while you watch."
            points={[
              "See changes live in your browser",
              "Sign in locally as a User or an Admin",
              "Iterate until it feels right",
            ]}
            isVideo
            img={imgStep4}
            imgAlt="Building the app with Claude"
          />
          <FlowStep
            number={5}
            title="Publish and it ships"
            intro="There is no separate deploy step to learn: pushing is deploying."
            points={[
              "Publish to GitHub when you are happy",
              "It goes live automatically",
              "No servers, no config, no ops",
            ]}
            isVideo
            img={imgStep5}
            imgAlt="Pushing to GitHub triggers the deploy automatically"
          />
          <FlowStep
            number={6}
            title="It is live"
            intro="A real, shareable app, secure by default and always up to date."
            points={[
              "A real URL, ready to share",
              "If the New Project form asked for a web address, that is your app's address, and it can take up to 45 minutes to go live the first time",
              "Colleagues sign in with their Norican account",
              "Every push updates it instantly",
            ]}
            img={imgStep6}
            imgAlt="The finished app live on its own URL"
          />
        </div>

        <Tip>
          That is it: a real app your colleagues can log into, and every change you push just
          updates it.
        </Tip>
      </div>
    ),
    searchItems: [
      { title: "The whole flow", keywords: ["flow", "steps", "overview", "how"] },
      { title: "Create your project", keywords: ["create", "new", "project", "name", "start"] },
      { title: "Watch it set itself up", keywords: ["setup", "repo", "hosting", "provision"] },
      {
        title: "Open it in Claude",
        keywords: ["claude", "prompt", "clone", "wsl", "set up in claude"],
      },
      { title: "Build it by vibecoding", keywords: ["build", "vibecode", "local", "iterate"] },
      { title: "Publish and it ships", keywords: ["publish", "push", "deploy", "ship"] },
      {
        title: "It is live",
        keywords: ["live", "url", "share", "sign in", "web address", "hostname", "domain"],
      },
    ],
  },

  /* ---- 5. AI-enabled projects ---- */
  {
    id: "ai-enabled",
    title: "AI-enabled projects",
    icon: Bot,
    description: "Turn on Foundry, Langfuse, and web search for your project",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Flip the <strong>AI-enabled</strong> switch when you create a project (step 2 of{" "}
          <em>The whole flow</em> above) and it is wired up with everything an AI feature needs:
          model access, tracing, and web search. It cannot be turned on later through Scaffold UI on
          its own, so enable it at creation when possible. An existing project can still get there
          through a Norican-assisted retrofit, see the tip below.
        </p>

        <div className="space-y-5">
          <Step number={1} title="Turn it on at creation">
            <p>
              In the project review step, switch on <strong>AI-enabled</strong>. This provisions
              Azure AI Foundry model access, Langfuse tracing, Serper web search, and a retrieval
              pipeline on the project&apos;s own database, no setup required afterwards.
            </p>
          </Step>

          <Step number={2} title="Fetch your AI secrets locally">
            <p>Once you have the project cloned (see Develop locally), run:</p>
            <CodeBlock code={"az login\nmake fetch-secrets"} />
            <p>
              This pulls the project&apos;s AI keys from its Azure Key Vault into your local{" "}
              <code>.env</code>. Values are never printed, only which keys were found.
            </p>
          </Step>

          <Step number={3} title="Build with the three model roles">
            <p>
              Claude already knows to reach for <strong>reasoning</strong>,{" "}
              <strong>extraction</strong>, and <strong>embedding</strong>, the three curated model
              roles, instead of hardcoding a model name. Just describe the AI feature you want.
            </p>
          </Step>

          <Step number={4} title="See your traces">
            <p>
              Every model call is traced to Langfuse automatically. During development, projects
              share one sandbox Langfuse project, filtered by a tag unique to your project, so you
              can tell your traces apart from everyone else&apos;s. <code>make fetch-secrets</code>{" "}
              does not retrieve that tag, though, so local traces are not tagged automatically. Ask
              the Norican team for your project&apos;s tag and add it to <code>.env</code> before
              tracing locally.
            </p>
          </Step>

          <BudgetStep />
        </div>

        <Tip>
          An admin can later promote an AI-enabled project to test/prod and link it to its own
          dedicated Langfuse project from the project&apos;s Details tab. Already have a project
          without AI? Running <code>/enable-ai</code> in that repository only updates its code,
          Scaffold UI still needs to know the project is AI-enabled for Foundry access and Langfuse
          linking to work, so ask the Norican team too, and have them backfill the <code>dev</code>{" "}
          Langfuse/Serper secrets and the <code>dev</code> Foundry endpoint/model secrets, since
          those are only provisioned once, at creation time. Then trigger another <code>dev</code>{" "}
          deployment, its successful callback is what grants your app&apos;s Foundry access and your
          own Key Vault access.
        </Tip>
      </div>
    ),
    searchItems: [
      {
        title: "AI-enabled projects",
        keywords: [
          "ai",
          "ai-enabled",
          "foundry",
          "azure ai foundry",
          "langfuse",
          "traces",
          "tracing",
          "serper",
          "web search",
          "rag",
          "retrieval",
          "fetch-secrets",
          "reasoning",
          "extraction",
          "embedding",
          "enable-ai",
        ],
      },
      {
        title: "Fetch your AI secrets",
        keywords: ["fetch-secrets", "az login", "key vault", "env"],
      },
      { title: "See your traces", keywords: ["langfuse", "traces", "cost", "sandbox"] },
      {
        title: "Keep an eye on your monthly budget",
        keywords: [
          "budget",
          "usage",
          "limit",
          "quota",
          "cost",
          "request more",
          "ai services",
          "suspended",
          "paused",
        ],
        servicesGatewayOnly: true,
      },
    ],
  },

  /* ---- 6. Requesting an app registration ---- */
  {
    id: "app-registration",
    title: "Requesting an app registration",
    icon: KeyRound,
    description: "When your app needs to sign people in or read their Microsoft data",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Most projects never need this. You need an app registration when your app has to
          <strong> sign people in with their Norican account</strong>, or has to read something of
          theirs from Microsoft 365 — their mail, their calendar, their files — while acting as
          them. If your app only ever talks to Azure as itself, it already has what it needs and you
          can skip this section.
        </p>
        <p className="text-muted-foreground">
          You ask from your own project, and someone in IT approves it. You never have to raise a
          ticket or know what to ask for.
        </p>

        <Tip>
          If your project signs people in with its own app registration, that registration is
          already in the list, marked <strong>Sign-in</strong>. To give your app Microsoft data
          access, use <strong>Change permissions</strong> on it instead of requesting a new one
          &mdash; see <strong>Sign-in and IT approval</strong> below.
        </Tip>

        <div className="space-y-5">
          <Step number={1} title="Open your project's Resources tab">
            <p>
              Click your project on the Home page, then the <strong>Resources</strong> tab, and find{" "}
              <strong>App registrations</strong>. Only the project&apos;s owner and admins can ask
              for one.
            </p>
          </Step>

          <Step number={2} title="Say what it is for">
            <p>
              Write a sentence on what the app does with the access. An actual person reads this
              before saying yes, so &ldquo;reads the team calendar to build the capacity
              report&rdquo; gets approved faster than &ldquo;needs Graph access&rdquo;.
            </p>
          </Step>

          <Step number={3} title="Answer the two questions">
            <p>
              Whether your app signs users in, and whether it then acts as the signed-in user. These
              decide whether you actually need an app registration at all.
            </p>
            <p className="text-muted-foreground">
              If the answers mean you don&apos;t, the form tells you so and names what to use
              instead. That is not an error and nothing is sent — it is the quickest possible answer
              to &ldquo;do I need this?&rdquo;.
            </p>
          </Step>

          <Step number={4} title="Pick the permissions">
            <p>
              The list opens on the permissions most apps ask for, grouped by what they touch. If
              yours is not there, type in the search box: it reaches every Microsoft Graph
              permission, by its name or by words in what it does (&ldquo;read calendars&rdquo;,
              &ldquo;Sites.Selected&rdquo;). Results marked <strong>Common</strong> are ones from
              the opening list.
            </p>
            <p>
              Each permission shows how sensitive it is and whether it needs a directory admin to
              sign off. Some also carry a red <strong>Privileged</strong> badge: they let your app
              manage who has access to what &mdash; give out permissions or roles, change group or
              team membership, change accounts and how people sign in, or manage devices &mdash;
              read secrets such as device passwords and recovery keys, or act as your mailbox: send
              mail as you, forward it, or open it to others.
            </p>
            <p className="text-muted-foreground">
              You can still ask for a privileged permission, but the approver has to tick that they
              have checked why your app needs exactly those before they can approve. Say why in your
              purpose, or expect the question.
            </p>
            <p className="text-muted-foreground">
              Ask for the least you need. A request for someone&apos;s whole mailbox when you only
              read their calendar is the most common reason one comes back to you.
            </p>
          </Step>

          <Step number={5} title="Choose how it proves who it is">
            <p>
              <strong>Federated</strong> is the default and the right answer almost always: your
              GitHub Actions workflow signs in with no secret to store or rotate. You give it the
              repository and branch it should trust.
            </p>
            <p>
              <strong>Client secret</strong> is for the cases federation cannot cover, and you have
              to say why. <strong>No credential</strong> is for an app that only signs users in and
              never calls anything as itself.
            </p>
          </Step>

          <Step number={6} title="Wait for approval">
            <p>
              Your request shows up in the Resources tab as <strong>Awaiting approval</strong>. When
              IT approves it, that becomes <strong>Provisioned</strong> and the application id
              appears there.
            </p>
            <p className="text-muted-foreground">
              If it says <strong>Provisioning failed</strong>, the approval still stands and IT can
              simply approve it again — nothing is lost and nothing gets created twice.
            </p>
          </Step>

          <Step number={7} title="Changing the permissions later">
            <p>
              Once it is provisioned, the registration shows a <strong>Change permissions</strong>{" "}
              button in the Resources tab. Use it to add a permission you turned out to need, or to
              drop one you do not. Say why, the same way you did the first time, and an approver
              decides.
            </p>
            <p className="text-muted-foreground">
              The registration itself does not change &mdash; same application, same application id,
              so nothing you have already wired up breaks. Only one change can be waiting at a time.
            </p>
            <p className="text-muted-foreground">
              Consent does not move on its own, in either direction. A permission you{" "}
              <strong>add</strong> will not work until a directory admin grants consent again. A
              permission you <strong>remove</strong> stops being listed, but the consent already
              given for it stays until an admin withdraws it &mdash; so if you are removing
              something because it should no longer be reachable, ask IT to revoke it too.
            </p>
          </Step>
          <Step number={8} title="When you no longer need it, give it back">
            <p>
              On the same panel, <strong>Request decommission</strong> asks for the app registration
              to be deleted. Like the original request, a Scaffold UI admin approves it before
              anything is deleted — you are asking, not deleting.
            </p>
            <p>
              You type the registration&apos;s name back to confirm, because each environment has
              its own and they differ by three letters. Only the one you name is affected.
            </p>
            <p className="text-muted-foreground">
              Once it is done, the application id stops working straight away. If your app still has
              it configured, take that setting out and deploy again — Scaffold UI cannot change your
              code or your deployment for you. Afterwards the environment is free to ask for a new
              app registration, if you need a different one.
            </p>
            <p className="text-muted-foreground">
              A project cannot be deleted while it still holds a live app registration: decommission
              it first. The project&apos;s own sign-in registration is the exception — it goes with
              the project.
            </p>
          </Step>
        </div>

        <div className="border-border bg-muted/40 space-y-2 rounded-xl border p-4">
          <p className="font-medium">One last step, and it is easy to miss</p>
          <p className="text-muted-foreground text-sm">
            A provisioned app exists but its permissions do not work yet. A directory admin has to
            grant consent, using the link shown with the approval. Until someone clicks it, signing
            in will fail with a consent error — so if your brand new app registration seems broken,
            this is almost always why.
          </p>
        </div>

        <p className="text-muted-foreground text-sm">
          If a client secret was created, it is shown once and never again. Put it straight into
          your repository&apos;s Actions secrets — if you lose it, ask for a new one rather than
          hunting for it.
        </p>
      </div>
    ),
    searchItems: [
      {
        title: "Requesting an app registration",
        keywords: [
          "app registration",
          "entra",
          "azure ad",
          "sign in",
          "sso",
          "login",
          "permissions",
          "graph",
          "microsoft 365",
          "mail",
          "calendar",
          "request",
          "awaiting approval",
          "pending",
          "provisioned",
        ],
      },
      {
        title: "Pick the permissions",
        keywords: [
          "scopes",
          "permissions",
          "search",
          "common",
          "risk",
          "privileged",
          "acknowledge",
          "admin consent",
          "delegated",
          "least",
        ],
      },
      {
        title: "When you no longer need it, give it back",
        keywords: [
          "decommission",
          "delete",
          "remove",
          "retire",
          "tear down",
          "no longer needed",
          "revoke",
          "clean up",
        ],
      },
      {
        title: "Federated or client secret",
        keywords: ["federated", "secret", "credential", "github actions", "oidc", "rotate"],
      },
      {
        title: "Changing the permissions later",
        keywords: [
          "change permissions",
          "edit permissions",
          "add a permission",
          "remove a permission",
          "amend",
          "update scopes",
          "more access",
          "less access",
        ],
      },
      {
        title: "Admin consent",
        keywords: ["consent", "grant", "directory admin", "not working", "consent error"],
      },
    ],
  },

  /* ---- 7. Sign-in and IT approval ---- */
  {
    id: "sign-in",
    title: "Sign-in and IT approval",
    icon: ShieldCheck,
    description: "When your project signs people in with its own app registration",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          New projects can sign people in with their <strong>own app registration</strong> &mdash;
          one for each environment (development, test and production) &mdash; instead of the sign-in
          every other project shares. Open your project and look at the <strong>Sign-in</strong> box
          on the <strong>Details</strong> tab to see which yours uses. If there is no such box, your
          project uses the shared sign-in and nothing here applies to it.
        </p>

        <div className="space-y-5">
          <Step number={1} title="Waiting for IT approval">
            <p>
              Every new sign-in app registration has to be approved by an IT admin before anyone can
              use it. Until then the Sign-in box says <strong>Waiting for IT approval</strong>, and
              nobody &mdash; you included &mdash; can sign in to that environment.
            </p>
            <p className="text-muted-foreground">
              You don&apos;t need to do anything: Scaffold UI admins see it in their list and get it
              approved. Scaffold UI notices the approval by itself, normally within a few minutes,
              and the box then says <strong>Approved by IT</strong> with the date.
            </p>
          </Step>

          <Step number={2} title="Giving your app Microsoft data access">
            <p>
              The same registration appears under <strong>App registrations</strong> on the{" "}
              <strong>Resources</strong> tab, marked <strong>Sign-in</strong>. If your app needs to
              read something from Microsoft 365 as the person using it &mdash; their calendar, their
              mail, their files &mdash; click <strong>Change permissions</strong> on it and add what
              you need.
            </p>
            <p className="text-muted-foreground">
              Don&apos;t request a new app registration for that environment. The form won&apos;t
              offer it: the permissions belong on the registration your users already sign in with.
            </p>
          </Step>

          <Step number={3} title="Added permissions need IT approval too">
            <p>
              Once an approver accepts your change, the registration goes back to IT for approval.
              Signing in keeps working the whole time; the new permissions start working once IT has
              approved them. Until then the Sign-in box says{" "}
              <strong>New permissions are waiting for IT approval</strong>.
            </p>
          </Step>

          <Step number={4} title="For Scaffold UI admins: the IT approvals page">
            <p>
              Everything waiting is listed under <strong>IT approvals</strong> in the sidebar. Send
              the <strong>Open admin consent</strong> link to a directory admin (a Global, Cloud
              Application or Privileged Role Administrator) or open it yourself if you are one, and
              accept. Links expire after an hour &mdash; reload the page for a fresh one.
            </p>
            <p>
              That&apos;s all: the page confirms the approval by itself, normally within a few
              minutes &mdash; also when IT approved it in the Entra portal instead of through the
              link. Until then it may say <strong>Reported, unverified</strong>.
            </p>
            <p className="text-muted-foreground">
              <strong>Check</strong> and <strong>Mark approved</strong> are fallbacks for when that
              doesn&apos;t happen. Check looks for the approval right away; Mark approved records it
              by hand with a note saying how you know &mdash; it only updates Scaffold UI&apos;s
              record and does not approve anything itself.
            </p>
          </Step>
        </div>

        <p className="text-muted-foreground text-sm">
          Project owners may also see a note that the project signs in with the shared Norican
          sign-in app, with a reason. That means its own sign-in could not be set up when it was
          created; the shared sign-in works exactly as before.
        </p>
      </div>
    ),
    searchItems: [
      {
        title: "Sign-in and IT approval",
        keywords: [
          "sign in",
          "sign-in",
          "login",
          "it approval",
          "waiting for it approval",
          "cannot sign in",
          "consent",
          "app registration",
          "own app registration",
          "shared sign-in",
        ],
      },
      {
        title: "Microsoft data access for sign-in",
        keywords: [
          "change permissions",
          "permissions",
          "graph",
          "microsoft 365",
          "calendar",
          "mail",
          "app registration",
        ],
      },
      {
        title: "IT approvals page",
        keywords: [
          "it approvals",
          "admin consent",
          "consent link",
          "mark approved",
          "check",
          "directory admin",
          "consent",
        ],
      },
    ],
  },

  /* ---- 8. Deleting a project (post ship) ---- */
  {
    id: "delete",
    title: "Deleting a project",
    icon: Trash2,
    description: "How to remove a project you no longer need",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          When you delete a project, remove its pieces in this order. You can do all of it from the
          project&apos;s detail page, under the Resources tab. Only project owners and admins can
          delete a project.
        </p>

        <div className="space-y-5">
          <Step number={1} title="Delete the Azure resources (optional)">
            <p>
              This takes down everything the app deployed to Azure (its resource group). To confirm,
              you will be asked to type the project&apos;s name. You can skip it: Remove Project
              deletes whatever is left in Azure.
            </p>
          </Step>
          <Step number={2} title="Delete the GitHub repository">
            <p>Remove the project&apos;s repository from Norican&apos;s GitHub organisation.</p>
          </Step>
          <Step number={3} title="Remove the project">
            <p>
              Finally, click <strong>Remove Project</strong>. Don&apos;t skip it: until you do, the
              project stays in Scaffold UI, and so do its sign-in app registrations, deploy identity
              and Entra groups. Remove Project also deletes any Azure resources that are left.
            </p>
            <p className="text-muted-foreground">
              If the project still holds an app registration it asked for, Remove Project is refused
              until that registration is decommissioned: request it on the project&apos;s App
              registrations panel, and a Scaffold UI admin approves it.
            </p>
          </Step>
        </div>

        <VideoDemo src={imgPostShip} label="The Scaffold UI projects overview" />

        <Tip>Deletion is permanent: none of these steps can be undone.</Tip>
      </div>
    ),
    searchItems: [
      {
        title: "Deleting a project",
        keywords: ["delete", "remove", "teardown", "azure", "resource", "repository"],
      },
    ],
  },

  /* ---- 9. FAQs ---- */
  {
    id: "faqs",
    title: "FAQs",
    icon: HelpCircle,
    description: "Common questions and troubleshooting",
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">Answers to the most common questions.</p>

        <Accordion type="multiple" defaultValue={["faq-1"]} className="w-full">
          <AccordionItem value="faq-1">
            <AccordionTrigger>How do I connect my GitHub account?</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                On the home page, click &ldquo;Connect GitHub&rdquo; in the green banner at the top.
                You&apos;ll be redirected to GitHub to authorise the Norican app, then sent back,
                and you can start creating projects.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-2">
            <AccordionTrigger>My project creation failed, what do I do?</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  Check the error on the project card. Common causes are a temporary GitHub rate
                  limit or a step timing out. You can try creating the project again.
                </p>
                <p>
                  If it persists, open the project card and check the &ldquo;Logs&rdquo; tab for
                  detail, then reach out to the Norican team.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-3">
            <AccordionTrigger>Can I open the project without the Claude prompt?</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  The easiest way is the &ldquo;Set up in Claude&rdquo; prompt (step 3 above). If
                  you prefer to do it by hand, from a terminal in your Ubuntu WSL:
                </p>
                <CodeBlock
                  code={
                    "git clone https://github.com/NO-GH-ORG-Workbench/your-project.git\ncd your-project\nmake prepare   # installs everything\nmake dev       # starts it locally"
                  }
                />
                <p>
                  Then open <ExternalLink href="http://localhost:3000">localhost:3000</ExternalLink>{" "}
                  in your browser.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-4">
            <AccordionTrigger>Can I change the project name after creation?</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                The name and slug are set at creation and tied to the GitHub repository name. If you
                need a different name, it&apos;s easiest to create a new project.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-5">
            <AccordionTrigger>How does sign-in to my app work?</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  Every scaffolded app ships with a full sign-in system, and the{" "}
                  <strong>backend is in charge of it</strong>. Colleagues sign in with their Norican
                  account; the backend checks them and hands the browser a secure session, then
                  verifies it on every request.
                </p>
                <p>
                  The backend is never exposed to the public internet, requests always reach it
                  through the frontend. So it is protected both ways.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-6">
            <AccordionTrigger>How do I get my project to test or prod?</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  Moving a project to test (and later to prod) is done by a Scaffold UI admin. Ask
                  for it, saying why it is ready. If you work in Claude with the ScaffoldUI
                  connection, you can simply ask Claude to request the promotion for you.
                </p>
                <p>
                  Admins see every request under <strong>Admin &rarr; Promotion Requests</strong>.
                  Approving one promotes the project there and then; a rejected request comes with a
                  note saying what to change. The new environment shows up on your project card once
                  it is promoted.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    ),
    searchItems: [
      {
        title: "Promote to test or prod",
        keywords: ["promote", "promotion", "test", "prod", "production", "request", "approve"],
      },
      { title: "Connect GitHub account", keywords: ["github", "connect", "authorise", "banner"] },
      { title: "Project creation failed", keywords: ["fail", "error", "timeout", "logs"] },
      {
        title: "Open the project manually",
        keywords: ["clone", "manual", "make", "prepare", "dev", "local"],
      },
      { title: "Change project name", keywords: ["rename", "change", "name", "slug"] },
      { title: "How sign-in works", keywords: ["auth", "login", "session", "security", "backend"] },
    ],
  },
];
